import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import Layout from './Layout';
import { formatCurrency } from '../utils/formatters';
import taxService, { TaxCalculation, AddressValidationResponse } from '../services/taxService';
import './Cart.css';
import './Checkout.css';

interface CartItem {
  id: string;
  product: {
    id: number;
    sku: string;
    name: string;
    image_url?: string;
    current_price: number;
    available_quantity: number;
    minimum_order_quantity: number;
  };
  quantity: number;
  price_at_time: number;
  subtotal: number;
}

interface CartData {
  id: string;
  item_count: number;
  subtotal: number;
}

interface ShippingAddress {
  business_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

/* ====== BILLING ADDRESS - COMMENTED OUT FOR EASY RESTORATION ======
interface BillingAddress {
  business_name: string;
  contact_name: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}
====== END BILLING ADDRESS INTERFACE ====== */

interface CheckoutProps {
  isAnonymous?: boolean;
  shareToken?: string;
}

export const Checkout: React.FC<CheckoutProps> = ({ isAnonymous = false, shareToken }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { draftId } = useParams<{ draftId?: string }>();
  const isDraftCheckout = !!draftId;

  // Core states
  const [cart, setCart] = useState<CartData | null>(null);
  const [items, setItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [draftData, setDraftData] = useState<any>(null);
  
  // Form states
  const [paymentMethod, setPaymentMethod] = useState<'ach' | 'credit_debit' | 'purchase_order'>('ach');
  const [poNumber, setPoNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [convenienceFeePercentage, setConvenienceFeePercentage] = useState(0.03); // Default 3%
  const [minimumOrderAmount, setMinimumOrderAmount] = useState(100); // Default $100
  const [shippingMinimumDollars, setShippingMinimumDollars] = useState(12); // Default $12
  const [shippingPercentage, setShippingPercentage] = useState(0.1); // Default 10%
  /* ====== BILLING ADDRESS - COMMENTED OUT ======
  const [sameAsShipping, setSameAsShipping] = useState(true);
  ====== END BILLING ADDRESS STATE ====== */
  
  // Tax states
  const [taxCalculation, setTaxCalculation] = useState<TaxCalculation | null>(null);
  const [taxLoading, setTaxLoading] = useState(false);
  const [taxDebounceTimer, setTaxDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [taxError, setTaxError] = useState<string>('');
  const [taxAttempted, setTaxAttempted] = useState(false);
  const [isTaxExempt, setIsTaxExempt] = useState(false);
  
  // Address validation states
  const [addressValidation, setAddressValidation] = useState<AddressValidationResponse | null>(null);
  const [addressValidating, setAddressValidating] = useState(false);
  const [addressDebounceTimer, setAddressDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [showAddressSuggestion, setShowAddressSuggestion] = useState(false);
  
  // Address states
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    business_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    country: 'US'
  });

  /* ====== BILLING ADDRESS - COMMENTED OUT ======
  const [billingAddress, setBillingAddress] = useState<BillingAddress>({
    business_name: '',
    contact_name: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    country: 'US'
  });
  ====== END BILLING ADDRESS STATE ====== */

  const isDesktop = windowWidth >= 992;

  // Effect hooks
  useEffect(() => {
    if (isAnonymous) {
      // Load shared quote data from sessionStorage
      fetchAnonymousDraft();
    } else if (user) {
      if (isDraftCheckout && draftId) {
        fetchDraft();
      } else {
        fetchCart();
      }
    }
  }, [user, isDraftCheckout, draftId, isAnonymous]);

  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      setWindowWidth(newWidth);
      console.log('Window resized to:', newWidth, 'isDesktop:', newWidth >= 992);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch system settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/settings/grouped');
        setConvenienceFeePercentage(response.data.convenience_fee_percentage);
        setMinimumOrderAmount(response.data.minimum_order_amount);
        setShippingMinimumDollars(response.data.shipping_minimum_dollars);
        setShippingPercentage(response.data.shipping_percentage);
      } catch (error) {
        console.error('Failed to load settings:', error);
        // Keep default values
      }
    };

    fetchSettings();
  }, []);

  /* ====== BILLING ADDRESS - COMMENTED OUT ======
  useEffect(() => {
    if (sameAsShipping) {
      setBillingAddress({
        business_name: shippingAddress.business_name,
        contact_name: shippingAddress.contact_name,
        street: shippingAddress.street,
        city: shippingAddress.city,
        state: shippingAddress.state,
        zip: shippingAddress.zip,
        country: shippingAddress.country
      });
    }
  }, [sameAsShipping, shippingAddress]);
  ====== END BILLING ADDRESS SYNC ====== */

  useEffect(() => {
    if (user?.sites?.[0]?.qbocustidno === '865') {
      setIsTaxExempt(true);
    }
  }, [user]);

  // Pre-populate addresses from user's site data
  useEffect(() => {
    if (user && (user as any)?.sites?.[0] && !isAnonymous) {
      const site = (user as any).sites[0];

      // Pre-fill shipping address from site ship-to data
      const shippingAddr = {
        business_name: site.sitename || '',
        contact_name: `${(user as any).first_name || ''} ${(user as any).last_name || ''}`.trim() || (user as any).username || '',
        contact_email: user.email || '',
        contact_phone: site.fdphone || '',
        street: site.shipto_street || site.address || '',
        city: site.shipto_city || site.city || '',
        state: site.shipto_provstate || site.state || '',
        zip: site.shipto_postzip || site.zip_code || '',
        country: site.shipto_country || site.country || 'US'
      };

      setShippingAddress(shippingAddr);

      // Check if address is incomplete
      if (!shippingAddr.street || !shippingAddr.city || !shippingAddr.state || !shippingAddr.zip) {
        setError('Your site address is incomplete in our system. Please contact PureHD support at (888) 787-3431 to update your address before placing an order.');
      }

      /* ====== BILLING ADDRESS - COMMENTED OUT ======
      // Pre-fill billing address from site bill-to data
      setBillingAddress({
        business_name: site.billto_name || site.sitename || '',
        contact_name: `${(user as any).first_name || ''} ${(user as any).last_name || ''}`.trim() || (user as any).username || '',
        street: site.billto_street || site.address || '',
        city: site.billto_city || site.city || '',
        state: site.billto_provstate || site.state || '',
        zip: site.billto_postzip || site.zip_code || '',
        country: site.billto_country || site.country || 'US'
      });
      ====== END BILLING ADDRESS PRE-FILL ====== */
    }
  }, [user, isAnonymous]);

  // Auto-validate address when complete
  useEffect(() => {
    const isComplete = shippingAddress.business_name &&
                      shippingAddress.contact_name &&
                      shippingAddress.contact_email &&
                      shippingAddress.contact_phone &&
                      shippingAddress.street &&
                      shippingAddress.city &&
                      shippingAddress.state &&
                      shippingAddress.zip;

    const isValidZip = /^\d{5}$/.test(shippingAddress.zip);
    const isValidState = /^[A-Z]{2}$/.test(shippingAddress.state);
    const isValidCity = shippingAddress.city.length >= 2 && /^[a-zA-Z\s\-']+$/.test(shippingAddress.city);

    if (isComplete && isValidZip && isValidState && isValidCity) {
      setAddressValidation(null);
      if (addressDebounceTimer) clearTimeout(addressDebounceTimer);
      
      const timer = setTimeout(() => validateAddressWithAvalara(), 1500);
      setAddressDebounceTimer(timer);
    } else {
      if (addressDebounceTimer) clearTimeout(addressDebounceTimer);
      setAddressValidation(null);
    }

    return () => {
      if (addressDebounceTimer) clearTimeout(addressDebounceTimer);
    };
  }, [shippingAddress]);

  // Auto-calculate tax
  useEffect(() => {
    const isComplete = shippingAddress.business_name &&
                      shippingAddress.contact_name &&
                      shippingAddress.contact_email &&
                      shippingAddress.contact_phone &&
                      shippingAddress.street &&
                      shippingAddress.city &&
                      shippingAddress.state &&
                      shippingAddress.zip;

    const isValidZip = /^\d{5}$/.test(shippingAddress.zip);
    const isValidState = /^[A-Z]{2}$/.test(shippingAddress.state);
    const isValidCity = shippingAddress.city.length >= 2 && /^[a-zA-Z\s\-']+$/.test(shippingAddress.city);
    
    if (isComplete && isValidZip && isValidState && isValidCity && items.length > 0) {
      if (taxDebounceTimer) clearTimeout(taxDebounceTimer);
      
      const timer = setTimeout(() => calculateTax(), 1000);
      setTaxDebounceTimer(timer);
    } else {
      if (taxDebounceTimer) clearTimeout(taxDebounceTimer);
      if (taxCalculation && (!isComplete || !isValidZip || !isValidState || !isValidCity)) {
        setTaxCalculation(null);
        setTaxAttempted(false);
      }
    }

    return () => {
      if (taxDebounceTimer) clearTimeout(taxDebounceTimer);
    };
  }, [shippingAddress, items]);

  // Functions
  const fetchCart = async () => {
    try {
      const response = await api.get('/cart/');
      setCart(response.data.cart);
      // Sort items by price descending
      const sortedItems = (response.data.items || []).sort((a: CartItem, b: CartItem) => {
        return b.price_at_time - a.price_at_time;
      });
      setItems(sortedItems);
      setError('');
    } catch (err: any) {
      // Don't show error for session expiration during initial load
      if (err.response?.status === 401) {
        console.log('Session expired, user needs to login');
      } else {
        setError('Failed to load cart');
        console.error('Cart fetch error:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDraft = async () => {
    try {
      const response = await api.get(`/drafts/${draftId}`);
      setDraftData(response.data);

      // Convert draft items to cart item format for display
      const draftItems = response.data.items.map((item: any) => ({
        id: item.id,
        product: {
          id: item.product.id,
          sku: item.product.sku,
          name: item.product.name,
          image_url: item.product.image_url,
          current_price: item.unit_price,
          available_quantity: 999,
          minimum_order_quantity: 1
        },
        quantity: item.quantity,
        price_at_time: item.unit_price,
        subtotal: item.total_price
      }));

      // Sort items by price descending
      const sortedItems = draftItems.sort((a: CartItem, b: CartItem) => {
        return b.price_at_time - a.price_at_time;
      });

      setItems(sortedItems);
      setCart({
        id: response.data.draft.draft_id,
        item_count: draftItems.length,
        subtotal: response.data.draft.subtotal
      });
      setError('');
    } catch (err: any) {
      setError('Failed to load draft');
      console.error('Draft fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnonymousDraft = async () => {
    try {
      const storedData = sessionStorage.getItem('sharedDraftData');
      if (!storedData) {
        setError('No draft data found. Please go back to the draft page.');
        setLoading(false);
        return;
      }

      const data = JSON.parse(storedData);

      // Convert shared quote items to cart item format
      const quoteItems = data.items.map((item: any) => ({
        id: item.id,
        product: {
          id: item.product.id,
          sku: item.product.sku,
          name: item.product.name,
          image_url: item.product.image_url,
          current_price: item.unit_price,
          available_quantity: 999,
          minimum_order_quantity: 1
        },
        quantity: item.quantity,
        price_at_time: item.unit_price,
        subtotal: item.total_price
      }));

      // Sort items by price descending
      const sortedItems = quoteItems.sort((a: CartItem, b: CartItem) => {
        return b.price_at_time - a.price_at_time;
      });

      setItems(sortedItems);
      setCart({
        id: data.quote_id,
        item_count: quoteItems.length,
        subtotal: data.subtotal
      });

      // Pre-fill shipping address if available (company name + site address + contact info)
      if (data.company_name || data.shipping_address) {
        // Format state to uppercase and zip to 5 digits for validation
        let state = (data.shipping_address?.state || '').toUpperCase().trim();
        let zip = (data.shipping_address?.zip || '').replace(/\D/g, '').slice(0, 5);

        setShippingAddress(prev => ({
          ...prev,
          business_name: data.company_name || '',
          contact_name: data.contact_name || '',
          contact_email: data.contact_email || '',
          contact_phone: data.contact_phone || '',
          street: data.shipping_address?.street || '',
          city: data.shipping_address?.city || '',
          state: state,
          zip: zip,
          country: data.shipping_address?.country || 'US'
        }));
      }

      setError('');
    } catch (err: any) {
      setError('Failed to load draft data');
      console.error('Anonymous quote fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    if (!shippingAddress.business_name || !shippingAddress.contact_name ||
        !shippingAddress.contact_email || !shippingAddress.contact_phone ||
        !shippingAddress.street || !shippingAddress.city ||
        !shippingAddress.state || !shippingAddress.zip) {
      setError('Please fill in all shipping address fields');
      return false;
    }

    /* ====== BILLING ADDRESS - COMMENTED OUT ======
    if (!sameAsShipping) {
      if (!billingAddress.business_name || !billingAddress.contact_name ||
          !billingAddress.street || !billingAddress.city ||
          !billingAddress.state || !billingAddress.zip) {
        setError('Please fill in all billing address fields');
        return false;
      }
    }
    ====== END BILLING ADDRESS VALIDATION ====== */

    if (cart && cart.subtotal < minimumOrderAmount) {
      setError(`Minimum order amount is $${minimumOrderAmount.toFixed(2)}`);
      return false;
    }

    if (addressValidating) {
      setError('Address validation in progress. Please wait.');
      return false;
    }

    if (showAddressSuggestion) {
      setError('Please confirm your shipping address selection.');
      return false;
    }

    if (addressValidation?.validation_status === 'undeliverable') {
      setError('The shipping address could not be verified. Please correct it.');
      return false;
    }

    return true;
  };

  const validateAddressWithAvalara = async () => {
    setAddressValidating(true);
    setShowAddressSuggestion(false);

    try {
      let result;

      if (isAnonymous) {
        // Use guest address validation endpoint
        const response = await fetch('/api/guest/validate-address', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            shipping_address: {
              business_name: shippingAddress.business_name,
              contact_name: shippingAddress.contact_name,
              street: shippingAddress.street,
              city: shippingAddress.city,
              state: shippingAddress.state,
              zip: shippingAddress.zip,
              country: shippingAddress.country
            }
          })
        });

        if (!response.ok) {
          throw new Error('Address validation failed');
        }

        result = await response.json();
      } else {
        // Use authenticated address validation endpoint
        result = await taxService.validateAddress(shippingAddress);
      }

      setAddressValidation(result);

      if (result.validation_status === 'corrected' && result.suggested_address) {
        setShowAddressSuggestion(true);
      } else {
        setShowAddressSuggestion(false);
      }
    } catch (error) {
      console.error('Address validation error:', error);
      setShowAddressSuggestion(false);
    } finally {
      setAddressValidating(false);
    }
  };

  const acceptSuggestedAddress = () => {
    if (addressValidation?.suggested_address) {
      setShippingAddress({
        ...shippingAddress,
        street: addressValidation.suggested_address.street,
        city: addressValidation.suggested_address.city,
        state: addressValidation.suggested_address.state,
        zip: addressValidation.suggested_address.zip
      });
      setShowAddressSuggestion(false);
      setTaxCalculation(null);
      setTaxAttempted(false);
    }
  };

  const keepOriginalAddress = () => {
    setShowAddressSuggestion(false);
  };

  const calculateTax = async () => {
    setTaxLoading(true);
    setTaxError('');
    setTaxAttempted(true);
    setIsTaxExempt(false);

    try {
      if (isAnonymous) {
        // Anonymous guest checkout - use guest checkout tax endpoint
        if (!shareToken) {
          setTaxError('Session expired. Please go back to the draft page.');
          setTaxLoading(false);
          return;
        }

        // Build guest info from shipping address
        const guestInfo = {
          full_name: shippingAddress.contact_name,
          email: shippingAddress.contact_email,
          phone: shippingAddress.contact_phone,
          company_name: shippingAddress.business_name
        };

        const response = await fetch('/api/guest/tax/calculate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            share_token: shareToken,
            shipping_address: {
              business_name: shippingAddress.business_name,
              contact_name: shippingAddress.contact_name,
              street: shippingAddress.street,
              city: shippingAddress.city,
              state: shippingAddress.state,
              zip: shippingAddress.zip,
              country: shippingAddress.country
            },
            guest_info: guestInfo
          })
        });

        if (!response.ok) {
          throw new Error('Tax calculation failed');
        }

        const data = await response.json();

        if (data.success) {
          setTaxCalculation({
            total_tax: data.tax_amount,
            total_amount: data.total_amount,
            taxable_amount: data.subtotal,
            tax_rate: 0,
            tax_breakdown: data.tax_breakdown || []
          });
          setTaxError('');
          if (data.tax_amount === 0 && subtotal > 0) {
            setIsTaxExempt(true);
          }
        } else {
          setTaxCalculation(null);
          setTaxError(data.warning || 'Tax calculation unavailable. You may proceed with checkout.');
        }
      } else {
        // Authenticated user checkout - use regular tax endpoint
        const qboCustomerId = user?.sites?.[0]?.qbocustidno || '375';

        if (qboCustomerId === '865') {
          setIsTaxExempt(true);
          setTaxCalculation({
            total_tax: 0,
            total_amount: subtotal,
            taxable_amount: subtotal,
            tax_rate: 0,
            tax_breakdown: []
          });
          setTaxError('');
          setTaxLoading(false);
          return;
        }

        const taxItems = items.map(item => ({
          sku: item.product.sku,
          quantity: item.quantity,
          amount: item.price_at_time,
          tax_code: 'P0000000',
          description: item.product.name
        }));

        const response = await taxService.validateAndCalculateTax({
          qbo_customer_id: qboCustomerId,
          shipping_address: {
            ...shippingAddress,
            contact_name: shippingAddress.contact_name || ''
          },
          items: taxItems,
          commit_transaction: false
        });

        if (response.success) {
          setTaxCalculation(response.tax_calculation);
          setTaxError('');
          if (response.tax_calculation.total_tax === 0 && subtotal > 0) {
            setIsTaxExempt(true);
          }
        } else {
          setTaxCalculation(null);
          setTaxError('Tax calculation unavailable. You may proceed with checkout.');
        }
      }
    } catch (error: any) {
      console.error('Tax calculation error:', error);
      setTaxCalculation(null);
      setTaxError('Tax service temporarily unavailable. You may proceed with checkout.');
    } finally {
      setTaxLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setProcessing(true);
    setError('');

    try {
      if (isAnonymous) {
        // Anonymous guest checkout
        const shareToken = sessionStorage.getItem('shareToken');
        if (!shareToken) {
          setError('Session expired. Please go back to the draft page.');
          setProcessing(false);
          return;
        }

        const response = await fetch('/api/guest/checkout/session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            share_token: shareToken,
            payment_method: paymentMethod,
            shipping_address: shippingAddress,
            guest_info: {
              full_name: shippingAddress.contact_name,
              email: shippingAddress.contact_email,
              phone: shippingAddress.contact_phone,
              company_name: shippingAddress.business_name
            },
            notes: notes || undefined
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.detail || 'Failed to create checkout session');
        }

        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else if (isDraftCheckout && draftId) {
        // For draft checkout, create Stripe session directly (like cart checkout)
        const checkoutPayload = {
          draft_id: draftId,
          payment_method: paymentMethod,
          shipping_address: shippingAddress,
          /* ====== BILLING ADDRESS - COMMENTED OUT ======
          billing_address: sameAsShipping ? shippingAddress : billingAddress,
          ====== END BILLING ADDRESS ====== */
          billing_address: shippingAddress, // Using shipping address as billing for now
          notes: notes || null,
          tax_amount: taxCalculation?.total_tax || null,
          tax_details: taxCalculation ? {
            tax_breakdown: taxCalculation.tax_breakdown,
            tax_rate: taxCalculation.tax_rate,
            taxable_amount: taxCalculation.taxable_amount
          } : null
        };

        console.log('Creating checkout session from draft:', checkoutPayload);
        const response = await api.post('/payments/checkout-session-from-draft', checkoutPayload);

        // Redirect directly to Stripe checkout (no intermediate page)
        window.location.href = response.data.checkout_url;
      } else {
        // Create Stripe checkout session directly from cart
        const checkoutPayload = {
          payment_method: paymentMethod,
          shipping_address: shippingAddress,
          /* ====== BILLING ADDRESS - COMMENTED OUT ======
          billing_address: sameAsShipping ? shippingAddress : billingAddress,
          ====== END BILLING ADDRESS ====== */
          billing_address: shippingAddress, // Using shipping address as billing for now
          notes: notes || null,
          qbo_customer_id: user?.sites?.[0]?.qbocustidno || '375',
          tax_amount: taxCalculation?.total_tax || null,
          tax_details: taxCalculation ? {
            tax_breakdown: taxCalculation.tax_breakdown,
            tax_rate: taxCalculation.tax_rate,
            taxable_amount: taxCalculation.taxable_amount
          } : null
        };

        console.log('Creating checkout session:', checkoutPayload);
        const response = await api.post('/payments/checkout-session', checkoutPayload);

        // Redirect directly to Stripe checkout
        window.location.href = response.data.checkout_url;
      }
    } catch (err: any) {
      let errorMessage = err.response?.data?.detail || err.message || 'Failed to create order';

      // If errorMessage is an object, stringify it properly
      if (typeof errorMessage === 'object') {
        errorMessage = JSON.stringify(errorMessage);
      }

      // Check if draft was already converted to an order
      if (errorMessage.includes('Order ID:')) {
        const orderIdMatch = errorMessage.match(/Order ID: ([a-f0-9-]+)/);
        if (orderIdMatch) {
          const orderId = orderIdMatch[1];
          console.log(`Draft already converted to order ${orderId}, redirecting...`);
          navigate(`/orders/${orderId}`);
          return;
        }
      }

      setError(errorMessage);
      console.error('Order creation error:', err);
    } finally {
      setProcessing(false);
    }
  };

  // Calculate totals
  const subtotal = cart?.subtotal || 0;
  const shipping = Math.max(shippingMinimumDollars, subtotal * shippingPercentage);
  const tax = taxCalculation?.total_tax || 0;
  const taxCalculated = taxCalculation !== null && taxCalculation.total_tax !== undefined;
  const convenienceFee = paymentMethod === 'credit_debit' ? (subtotal + shipping + tax) * convenienceFeePercentage : 0;
  const total = taxCalculated ? (subtotal + shipping + tax + convenienceFee) : null;

  // Loading state
  if (loading) {
    return (
      <Layout>
        <div className="container mt-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading checkout...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Not logged in (skip this check for anonymous mode)
  if (!user && !isAnonymous) {
    return (
      <Layout>
        <div className="container mt-5">
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Please log in to proceed with checkout
          </div>
        </div>
      </Layout>
    );
  }

  // Empty cart
  if (items.length === 0) {
    return (
      <Layout>
        <div className="container mt-5">
          <div className="alert alert-info">
            <i className="fas fa-info-circle me-2"></i>
            Your cart is empty. Add items before proceeding to checkout.
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/products')}
          >
            <i className="fas fa-box me-2"></i>Browse Products
          </button>
        </div>
      </Layout>
    );
  }

  // Main checkout render - render form content
  const checkoutContent = (
    <div className="container mt-4">
        <h1 className="mb-4" style={{
          fontSize: '28px',
          fontWeight: '600',
          color: '#2c3e50'
        }}>
          <i className="fas fa-shopping-cart me-2"></i>
          {isAnonymous ? 'Guest Checkout' : (isDraftCheckout ? 'Draft Checkout' : 'Checkout')}
        </h1>

        {(isDraftCheckout || isAnonymous) && (
          <div className="alert alert-info mb-3 text-start" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span><i className="fas fa-info-circle me-2"></i><strong>Draft Order</strong></span>
            <span>You are checking out a draft with locked prices. The original draft prices will be honored.</span>
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <i className="fas fa-exclamation-circle me-2"></i>{error}
            <button type="button" className="btn-close" onClick={() => setError('')}></button>
          </div>
        )}
        
        {user?.sites?.[0]?.qbocustidno === '865' && (
          <div className="alert alert-info mb-3">
            <i className="fas fa-certificate me-2"></i>
            <strong>Tax-Exempt Customer:</strong> Your account has tax-exempt status. No sales tax will be charged on your orders.
          </div>
        )}

        <div style={{ 
          display: 'flex',
          flexWrap: isDesktop ? 'nowrap' : 'wrap',
          gap: '30px'
        }}>
          {/* Left Column - Addresses Only */}
          <div style={{
            flex: isDesktop ? '0 0 60%' : '0 0 100%',
            maxWidth: isDesktop ? 'calc(60% - 15px)' : '100%'
          }}>
            {/* Address Cards Container - Using flex for proper side-by-side layout */}
            <div style={{
              display: 'flex',
              flexWrap: isDesktop ? 'nowrap' : 'wrap',
              gap: '20px',
              marginBottom: '20px'
            }}>
              {/* Shipping Address Card */}
              <div style={{
                flex: isDesktop ? '1' : '0 0 100%',
                minWidth: isDesktop ? '0' : '100%'
              }}>
                <div className="card h-100" style={{ minHeight: '450px' }}>
                  <div className="card-header bg-light d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      <i className="fas fa-truck me-2"></i>Shipping Address
                    </h5>
                    {addressValidating && (
                      <span className="badge bg-info">
                        <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                        Validating...
                      </span>
                    )}
                    {!addressValidating && addressValidation && (
                      <>
                        {addressValidation.validation_status === 'validated' && (
                          <span className="badge bg-success">
                            <i className="fas fa-check-circle me-1"></i>Verified
                          </span>
                        )}
                        {addressValidation.validation_status === 'corrected' && (
                          <span className="badge bg-warning">
                            <i className="fas fa-exclamation-triangle me-1"></i>Corrected
                          </span>
                        )}
                        {addressValidation.validation_status === 'undeliverable' && (
                          <span className="badge bg-danger">
                            <i className="fas fa-times-circle me-1"></i>Undeliverable
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="form-label">Business Name *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={shippingAddress.business_name}
                          onChange={(e) => setShippingAddress({...shippingAddress, business_name: e.target.value})}
                          placeholder="Hotel Name"
                          disabled={true}
                          readOnly
                          style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Contact Name *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={shippingAddress.contact_name}
                          onChange={(e) => setShippingAddress({...shippingAddress, contact_name: e.target.value})}
                          placeholder="John Doe"
                          disabled={processing || isAnonymous}
                          readOnly={isAnonymous}
                          style={isAnonymous ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : undefined}
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Contact Email *</label>
                        <input
                          type="email"
                          className="form-control"
                          value={shippingAddress.contact_email}
                          onChange={(e) => setShippingAddress({...shippingAddress, contact_email: e.target.value})}
                          placeholder="john.doe@hotel.com"
                          disabled={processing || isAnonymous}
                          readOnly={isAnonymous}
                          style={isAnonymous ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : undefined}
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Contact Phone *</label>
                        <input
                          type="tel"
                          className="form-control"
                          value={shippingAddress.contact_phone}
                          onChange={(e) => setShippingAddress({...shippingAddress, contact_phone: e.target.value})}
                          placeholder="(555) 123-4567"
                          disabled={processing || isAnonymous}
                          readOnly={isAnonymous}
                          style={isAnonymous ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : undefined}
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Street Address *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={shippingAddress.street}
                          onChange={(e) => setShippingAddress({...shippingAddress, street: e.target.value})}
                          placeholder="123 Main St"
                          disabled={true}
                          readOnly
                          style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label">City *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={shippingAddress.city}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^a-zA-Z\s\-']/g, '');
                            setShippingAddress({...shippingAddress, city: value});
                          }}
                          placeholder="New York"
                          disabled={true}
                          readOnly
                          style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label">State *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={shippingAddress.state}
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
                            setShippingAddress({...shippingAddress, state: value});
                          }}
                          placeholder="NY"
                          maxLength={2}
                          disabled={true}
                          readOnly
                          style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label">ZIP *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={shippingAddress.zip}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                            setShippingAddress({...shippingAddress, zip: value});
                          }}
                          placeholder="10001"
                          maxLength={5}
                          disabled={true}
                          readOnly
                          style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ====== BILLING ADDRESS CARD - COMMENTED OUT FOR EASY RESTORATION ======
              <div style={{
                flex: isDesktop ? '1' : '0 0 100%',
                minWidth: isDesktop ? '0' : '100%'
              }}>
                <div className="card h-100" style={{ minHeight: '450px' }}>
                  <div className="card-header bg-light">
                    <h5 className="mb-0">
                      <i className="fas fa-file-invoice-dollar me-2"></i>Billing Address
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="form-check mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="sameAsShipping"
                        checked={sameAsShipping}
                        onChange={(e) => setSameAsShipping(e.target.checked)}
                        disabled={processing}
                      />
                      <label className="form-check-label" htmlFor="sameAsShipping">
                        Same as shipping address
                      </label>
                    </div>

                    <div className="row g-3">
                      <div className="col-12">
                        <label className="form-label">Business Name *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={sameAsShipping ? shippingAddress.business_name : billingAddress.business_name}
                          onChange={(e) => setBillingAddress({...billingAddress, business_name: e.target.value})}
                          placeholder="Company Name"
                          disabled={sameAsShipping || processing}
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Contact Name *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={sameAsShipping ? shippingAddress.contact_name : billingAddress.contact_name}
                          onChange={(e) => setBillingAddress({...billingAddress, contact_name: e.target.value})}
                          placeholder="John Doe"
                          disabled={sameAsShipping || processing}
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Street Address *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={sameAsShipping ? shippingAddress.street : billingAddress.street}
                          onChange={(e) => setBillingAddress({...billingAddress, street: e.target.value})}
                          placeholder="123 Main St"
                          disabled={sameAsShipping || processing}
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label">City *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={sameAsShipping ? shippingAddress.city : billingAddress.city}
                          onChange={(e) => setBillingAddress({...billingAddress, city: e.target.value})}
                          placeholder="New York"
                          disabled={sameAsShipping || processing}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label">State *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={sameAsShipping ? shippingAddress.state : billingAddress.state}
                          onChange={(e) => setBillingAddress({...billingAddress, state: e.target.value})}
                          placeholder="NY"
                          maxLength={2}
                          disabled={sameAsShipping || processing}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label">ZIP *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={sameAsShipping ? shippingAddress.zip : billingAddress.zip}
                          onChange={(e) => setBillingAddress({...billingAddress, zip: e.target.value})}
                          placeholder="10001"
                          disabled={sameAsShipping || processing}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              ====== END BILLING ADDRESS CARD ====== */}
            </div>

            {/* Address Suggestion Alert */}
            {showAddressSuggestion && addressValidation?.suggested_address && (
              <div className="alert alert-warning mb-4">
                <h6 className="alert-heading">
                  <i className="fas fa-map-marked-alt me-2"></i>
                  Address Suggestion
                </h6>
                <p className="mb-3">We found a suggested address. Would you like to use it?</p>
                
                <div className="row">
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header bg-light">
                        <small className="fw-bold">You Entered:</small>
                      </div>
                      <div className="card-body small">
                        <div>{addressValidation.original_address.street}</div>
                        <div>{addressValidation.original_address.city}, {addressValidation.original_address.state} {addressValidation.original_address.zip}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border-success">
                      <div className="card-header bg-success text-white">
                        <small className="fw-bold">Suggested:</small>
                      </div>
                      <div className="card-body small">
                        <div>{addressValidation.suggested_address.street}</div>
                        <div>{addressValidation.suggested_address.city}, {addressValidation.suggested_address.state} {addressValidation.suggested_address.zip}</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-3">
                  <button 
                    className="btn btn-success btn-sm me-2"
                    onClick={acceptSuggestedAddress}
                    disabled={processing}
                  >
                    Use Suggested Address
                  </button>
                  <button 
                    className="btn btn-outline-secondary btn-sm"
                    onClick={keepOriginalAddress}
                    disabled={processing}
                  >
                    <i className="fas fa-times me-1"></i>Keep Original Address
                  </button>
                </div>
              </div>
            )}

            {/* Address Validation Warnings */}
            {addressValidation && !showAddressSuggestion && (
              <>
                {addressValidation.validation_status === 'undeliverable' && (
                  <div className="alert alert-danger mb-4">
                    <i className="fas fa-times-circle me-2"></i>
                    <strong>Undeliverable Address:</strong> Please check and correct your address.
                  </div>
                )}
              </>
            )}

            {/* Removed Payment Method from here - moved to right column */}

            {/* Order Notes Card */}
            <div className="card mb-4">
              <div className="card-header bg-light">
                <h5 className="mb-0">
                  <i className="fas fa-comment me-2"></i>Order Notes (Optional)
                </h5>
              </div>
              <div className="card-body">
                <textarea
                  className="form-control"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any special instructions or notes..."
                  disabled={processing}
                />
              </div>
            </div>
          </div>

          {/* Right Column - Order Summary & Payment */}
          <div style={{
            flex: isDesktop ? '0 0 40%' : '0 0 100%',
            maxWidth: isDesktop ? 'calc(40% - 15px)' : '100%'
          }}>
            {/* Order Summary Card */}
            <div className="card mb-3">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">Order Summary</h5>
              </div>
              <div className="card-body">
                <h6 className="mb-3">Items ({cart?.item_count})</h6>
                <div className="order-items-summary mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {items.map(item => (
                    <div key={item.id} className="item-row">
                      <div className="item-details">
                        <div className="item-name" title={item.product.name}>
                          {item.product.name}
                        </div>
                        <div className="item-quantity">Qty: {item.quantity}</div>
                      </div>
                      <div className="item-price">{formatCurrency(item.subtotal)}</div>
                    </div>
                  ))}
                </div>

                <hr />

                <div className="checkout-totals">
                  <div className="total-line">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(subtotal)}</span>
                  </div>
                  <div className="total-line">
                    <span>Shipping:</span>
                    <span>{formatCurrency(shipping)}</span>
                  </div>
                  <div className="total-line">
                    <span>Tax:</span>
                    <span>
                      {taxLoading ? (
                        <span className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Calculating...</span>
                        </span>
                      ) : taxCalculated ? (
                        isTaxExempt ? (
                          <span className="d-flex align-items-center justify-content-end">
                            {formatCurrency(tax)}
                            <span className="badge bg-success ms-2 small">Tax Exempt</span>
                          </span>
                        ) : (
                          formatCurrency(tax)
                        )
                      ) : (
                        <span className="text-muted small">To be calculated</span>
                      )}
                    </span>
                  </div>
                  {paymentMethod === 'credit_debit' && (
                    <div className="total-line" style={{ backgroundColor: '#ffffdf', padding: '8px', borderRadius: '4px', marginTop: '4px', marginBottom: '4px' }}>
                      <span style={{ color: '#cc8800' }}>
                        Convenience Fee ({(convenienceFeePercentage * 100).toFixed(0)}%):
                      </span>
                      <span style={{ color: '#cc8800', fontWeight: '500' }}>{formatCurrency(convenienceFee)}</span>
                    </div>
                  )}

                  {taxError && taxAttempted && (
                    <div className="alert alert-warning small py-2 mb-2" style={{ textAlign: 'left' }}>
                      <i className="fas fa-info-circle me-1"></i>
                      {taxError}
                    </div>
                  )}

                  <div className="total-line total-final">
                    <strong>Total:</strong>
                    <strong className="text-primary">
                    {total !== null ? (
                      formatCurrency(total)
                    ) : (
                      <span>
                        {formatCurrency(subtotal + shipping)}
                        <small className="text-muted"> + tax</small>
                      </span>
                    )}
                    </strong>
                  </div>
                </div>

                <hr className="my-3" />

                {/* Payment Method Section - Now inside order summary card */}
                <h6 className="mb-2">
                  <i className="fas fa-credit-card me-2"></i>Payment Method
                </h6>
                <div
                  className={`payment-option mb-2 ${paymentMethod === 'ach' ? 'selected' : ''}`}
                  onClick={() => !processing && setPaymentMethod('ach')}
                  style={{
                    cursor: processing ? 'not-allowed' : 'pointer',
                    padding: '12px',
                    border: paymentMethod === 'ach' ? '2px solid #28a745' : '2px solid #dee2e6',
                    borderRadius: '6px',
                    backgroundColor: paymentMethod === 'ach' ? '#f0fff4' : '#fff',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div className="payment-method-title">
                    <i className="fas fa-university text-success me-2"></i>
                    <span>ACH Bank Transfer</span>
                    <span className="badge bg-success ms-2">No Fee</span>
                    {paymentMethod === 'ach' && (
                      <i className="fas fa-check-circle text-success ms-auto" style={{ float: 'right' }}></i>
                    )}
                  </div>
                  <div className="payment-method-subtitle">
                    Direct bank transfer - No convenience fee
                  </div>
                </div>

                <div
                  className={`payment-option mb-2 ${paymentMethod === 'credit_debit' ? 'selected' : ''}`}
                  onClick={() => !processing && setPaymentMethod('credit_debit')}
                  style={{
                    cursor: processing ? 'not-allowed' : 'pointer',
                    padding: '12px',
                    border: paymentMethod === 'credit_debit' ? '2px solid #0d6efd' : '2px solid #dee2e6',
                    borderRadius: '6px',
                    backgroundColor: paymentMethod === 'credit_debit' ? '#eff6ff' : '#fff',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div className="payment-method-title">
                    <i className="fas fa-credit-card text-primary me-2"></i>
                    <span>Credit/Debit Card</span>
                    <span className="badge bg-warning ms-2">{(convenienceFeePercentage * 100).toFixed(0)}% Fee</span>
                    {paymentMethod === 'credit_debit' && (
                      <i className="fas fa-check-circle text-primary ms-auto" style={{ float: 'right' }}></i>
                    )}
                  </div>
                  <div className="payment-method-subtitle">
                    Visa, Mastercard, American Express, Discover
                  </div>
                </div>

                {paymentMethod === 'credit_debit' && (
                  <div className="alert mt-2 mb-2 py-2" style={{ backgroundColor: '#ffffdf', border: '1px solid #f0e68c' }}>
                    <div>
                      <strong style={{ color: '#cc8800', fontSize: '14px' }}>{(convenienceFeePercentage * 100).toFixed(0)}% convenience fee applies</strong>
                      <div className="small" style={{ color: '#cc8800' }}>A convenience fee of {formatCurrency(convenienceFee)} will be added to your total.</div>
                    </div>
                  </div>
                )}

                <div className="d-grid gap-2 mt-3">
                  <button
                    className="btn btn-success btn-lg checkout-button"
                    onClick={handleSubmit}
                    disabled={processing || items.length === 0 || !paymentMethod}
                  >
                    {processing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-lock me-2"></i>
                        Proceed to Payment - {total !== null ? formatCurrency(total) : 'Calculate Total'}
                      </>
                    )}
                  </button>
                  <button
                    className="btn btn-outline-secondary checkout-button"
                    onClick={() => navigate('/cart')}
                    disabled={processing}
                  >
                    <i className="fas fa-arrow-left me-2"></i>Back to Cart
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );

  // Conditionally wrap the content based on anonymous vs authenticated
  if (isAnonymous) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f8f9fa' }}>
        {/* Anonymous Header - Same as SharedQuote */}
        <div style={{
          background: '#4D64C1',
          height: '80px',
          display: 'flex',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 1040,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div className="container-fluid">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <a href="https://www.purehd.com" target="_blank" rel="noopener noreferrer">
                  <img
                    src="/img/PureHD_Logo.png"
                    alt="PureHD"
                    style={{
                      height: '45px',
                      filter: 'brightness(0) invert(1)'
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </a>
              </div>
              <div className="text-center">
                <span style={{
                  color: 'white',
                  fontSize: '16px'
                }}>
                  <i className="fas fa-phone-alt me-2"></i>
                  <span className="d-none d-sm-inline">(888) 787-3431</span>
                </span>
              </div>
              <div>
                <button
                  className="btn btn-outline-light"
                  onClick={() => navigate('/login')}
                  style={{ fontSize: '14px' }}
                >
                  <i className="fas fa-sign-in-alt me-2"></i>
                  Login
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-grow-1" style={{ padding: '30px 0' }}>
          {checkoutContent}
        </div>
      </div>
    );
  }

  // For authenticated users, render with Layout
  return (
    <Layout>
      {checkoutContent}
    </Layout>
  );
};

export default Checkout;